import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  createOrganization,
  deleteOrganizationLink,
  fetchOrganization,
  fetchOrganizations,
  grantOrganizationRole,
  inviteToOrganization,
  type InviteResult,
  revokeOrganizationRole,
  setOrganizationStripeAccount,
  upsertOrganizationLink,
  type OrganizationDetail,
  type OrganizationDetailResult,
  type OrganizationsResult,
  type WriteResult,
} from '../../lib/api/platformOrganizations';
import { fetchPlatformUsers, type PlatformUser } from '../../lib/api/platformUsers';
import { startImpersonation } from '../../lib/api/impersonation';
import { navigateToSurface } from '../../app/surface';
import { useImpersonation } from './useImpersonation';
import { useAsyncAction } from '../../lib/useAsyncAction';
import { useT } from '../../i18n';
import { safeId } from '../ui/labelledControl';

// ─────────────────────────────────────────────────────────────────────
// /sa → Organisasjoner. Centres and clubs; who holds which role in each;
// which clubs a centre has given access to its courts, on what terms.
//
// The platform view: a global administrator grants ANY organization role
// to ANY account here. The organization's own «Tilganger» page — a centre
// or club granting within what it holds — is a later slice.
//
// Every write is authorized again server-side; the `can…` props only decide
// what is offered. THREE of them, because the section's writes sit behind
// three different capabilities (#924, `saWriteCapabilities.ts`):
//
//   canWrite        setup: create, name/kind/payment account, club links
//                   (`platform.organizations.manage`)
//   canGrant        grant, revoke, invite (`platform.roles.manage`)
//   canImpersonate  «Jobb som» (`platform.impersonate_organization`)
// ─────────────────────────────────────────────────────────────────────

export interface OrganizationsSectionProps {
  /** Setup writes: `platform.organizations.manage`, per the SERVER's answer. */
  canWrite: boolean;
  /** Role grants, revokes and invitations: `platform.roles.manage`. Defaults to `canWrite`. */
  canGrant?: boolean;
  /** Starting an impersonation: `platform.impersonate_organization`. Defaults to `canWrite`. */
  canImpersonate?: boolean;
  /** Injectable for tests. */
  loadList?: typeof fetchOrganizations;
  loadDetail?: typeof fetchOrganization;
  loadUsers?: typeof fetchPlatformUsers;
  create?: typeof createOrganization;
  grant?: typeof grantOrganizationRole;
  invite?: typeof inviteToOrganization;
  revoke?: typeof revokeOrganizationRole;
  upsertLink?: typeof upsertOrganizationLink;
  removeLink?: typeof deleteOrganizationLink;
}

const KIND_LABEL: Record<string, string> = { CENTRE: 'Senter', CLUB: 'Klubb' };

/** The kind, as a small coloured pill — one colour per kind, used wherever a kind is named. */
function KindPill({ kind }: { kind: string }) {
  const centre = kind === 'CENTRE';
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-px font-mono text-[10px] uppercase tracking-wider ${
        centre ? 'border-sky-200 bg-sky-50 text-sky-900' : 'border-lime-deep/40 bg-lime-bg text-ink'
      }`}
    >
      {KIND_LABEL[kind] ?? kind}
    </span>
  );
}

/**
 * ONE BLOCK OF THE DETAIL PANEL: a heading in the product's words, one line
 * on what the block is for, and the content. The blocks are what gives the
 * panel its rhythm — before this, the headings were the same small mono
 * labels as the field captions, and the page read as one long form.
 */
function DetailBlock({
  title,
  lead,
  mark,
  children,
  testId,
}: {
  title: string;
  lead?: string;
  /** A small marker beside the heading — today only «ikke i kraft». */
  mark?: React.ReactNode;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <section className="border-t border-line pt-5 first:border-t-0 first:pt-0" data-testid={testId}>
      <h4 className="font-display text-sm font-semibold text-ink flex flex-wrap items-center gap-2">
        {title}
        {mark}
      </h4>
      {lead && <p className="mt-0.5 text-[11px] text-ink-3">{lead}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

// THE CENTRE–CLUB LINKS CARRIED A «ikke i kraft» MARK (#861) while no route
// used what a link gives. Slices 4c (booking) and 4d (activities, 2026-10-21)
// put every link right in force, so the mark is gone; the matrix reads
// `inForce` from the server and needs no change of its own.

/** A quiet panel for the forms that ADD something, so they read as a tool
 *  beside the list rather than as part of it. */
function AddPanel({
  title,
  lead,
  children,
  testId,
}: {
  title: string;
  lead?: string;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <div className="mt-4 rounded-lg border border-line bg-bg-soft/50 p-4" data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-2">{title}</div>
      {lead && <p className="mt-0.5 text-[11px] text-ink-3">{lead}</p>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

/** A small caption above a field, the same everywhere in the panel. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="block font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

/** The table used by every list in the panel. */
function PanelTable({
  columns,
  children,
  testId,
}: {
  columns: string[];
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-line">
      <table className="min-w-full text-xs" data-testid={testId}>
        <thead>
          <tr className="bg-bg-soft/70 text-left font-mono text-[10px] uppercase tracking-wider text-ink-3">
            {columns.map((c, i) => (
              <th key={i} className={`px-3 py-2 font-normal ${c === '' ? 'w-px' : ''}`}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function EmptyRow({ colSpan, children }: { colSpan: number; children: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-3 py-3 text-[11px] text-ink-3 italic">
        {children}
      </td>
    </tr>
  );
}
const SETTLEMENT_LABEL: Record<string, string> = {
  centre_invoice: 'På senterets faktura',
  direct: 'Direkte avtale med Starpoint',
};
const REFUSAL_TEXT: Record<string, string> = {
  slug_taken: 'Kortnavnet er allerede i bruk.',
  invalid_slug: 'Kortnavnet kan bare ha små bokstaver, tall og bindestrek.',
  unknown_role: 'Rollen kan ikke gis i denne organisasjonen.',
  unknown_user: 'Fant ikke kontoen.',
  not_a_centre: 'Bare et senter kan gi en klubb tilgang.',
  not_a_club: 'Tilgang kan bare gis til en klubb.',
  not_found: 'Fant ikke organisasjonen.',
  // #284. The second one is the refusal that matters: the account belongs to
  // another organization, and connecting it here would pay this organization's
  // revenue into that one's bank account.
  invalid_stripe_account: 'Kontoen må se ut som «acct_…». Sjekk at hele id-en er limt inn.',
  stripe_account_already_connected:
    'Kontoen er allerede koblet til en annen organisasjon. To organisasjoner kan ikke dele konto — da havner pengene hos den andre.',
  // Reached only if the page sends a repoint without confirming it — the server
  // refuses on its own authority, so this text is the backstop, not the flow.
  stripe_account_replace_not_confirmed:
    'Bytte av konto må bekreftes. Prøv igjen og bekreft byttet.',
  // Somebody else changed the account between this page reading it and this save.
  // Nothing was written — the point is to send the operator back to what is true
  // now, because the confirmation they gave was about an account that has moved.
  stripe_account_changed_concurrently:
    'Kontoen ble endret av noen andre mens du holdt på. Last siden på nytt og se hva som står der nå.',
};

function describe(res: WriteResult): string | null {
  if (res.state === 'ok') return null;
  if (res.state === 'refused') return REFUSAL_TEXT[res.reason] ?? `Serveren avslo (${res.reason}).`;
  if (res.state === 'forbidden') return 'Serveren avslo endringen.';
  return 'Kunne ikke lagre.';
}

/**
 * «ARBEID SOM ORGANISASJONEN» — the one entry point (RBAC-3).
 *
 * The whole product surface of impersonation, deliberately: platform →
 * organization, and nothing else. No user to pick, no role to pretend to, no
 * special mode. What a StarPoint administrator gets is administrative
 * authority over that organization's data for a short while, on the record.
 *
 * THE REASON IS ASKED FOR INLINE, not through `window.prompt`. The same
 * reasoning as `window.confirm` (DOMAIN_RULES §9): it is blocked in an iframe
 * and simply does nothing, which here would mean a support engineer clicking a
 * button that silently never starts anything.
 */
function WorkAsOrganization({
  organization,
  canWrite,
}: {
  organization: { id: string; kind: string; name: string };
  canWrite: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = useImpersonation();
  const uid = safeId('sa-work-as', useId());

  // Already operating this one — the banner is the control, not this.
  if (active?.organization.id === organization.id) return null;
  if (!canWrite) return null;

  const start = async () => {
    setBusy(true);
    setError(null);
    const out = await startImpersonation(organization.id, reason.trim());
    setBusy(false);
    if (out.error) {
      setError(out.error);
      return;
    }
    setOpen(false);
    setReason('');
    // AND GO THERE. The context is authority inside a club's data, and the
    // place that data lives is the club portal — staying on /sa with a banner
    // was the whole of what this did before, which is why it read as doing
    // nothing. A client-side transition, so the in-memory token survives the
    // move (see app/surface.ts).
    navigateToSurface('portal');
  };

  if (!open) {
    return (
      // `btn-outline`, NOT `btn-ghost` (#428 review). A ghost button is
      // transparent and borderless until hovered, so the single most
      // consequential action in the panel — taking administrative authority
      // inside a customer's organization — was indistinguishable from a line
      // of text. It is also invisible to anyone who never hovers: touch, and
      // keyboard users scanning for controls.
      <button
        type="button"
        className="btn-outline text-xs"
        data-testid="sa-work-as-org"
        onClick={() => setOpen(true)}
      >
        Arbeid som organisasjonen
      </button>
    );
  }

  return (
    <div className="rounded border border-line p-3" data-testid="sa-work-as-form">
      <p className="text-[11px] text-ink-3">
        Du får administrativ tilgang til {organization.name} i en kort periode. Det blir logget.
      </p>
      <label
        htmlFor={`${uid}-reason`}
        className="mt-2 block text-[11px] font-mono uppercase tracking-wider text-ink-3"
      >
        Årsak
      </label>
      <input
        id={`${uid}-reason`}
        // The refusal is about this field, so it is tied to it (#900).
        aria-describedby={error ? `${uid}-error` : undefined}
        className="input mt-1 w-full text-xs"
        value={reason}
        disabled={busy}
        data-testid="sa-work-as-reason"
        onChange={(e) => setReason(e.target.value)}
        placeholder="Supportsak #123 — undersøker bookingfeil"
      />
      {error && (
        <p
          id={`${uid}-error`}
          className="mt-2 text-[11px] text-rose"
          data-testid="sa-work-as-error"
        >
          {error === 'reason_required' ? 'Skriv en årsak.' : `Kunne ikke starte: ${error}`}
        </p>
      )}
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          className="btn-lime text-xs"
          disabled={busy || reason.trim() === ''}
          data-testid="sa-work-as-start"
          onClick={() => void start()}
        >
          {busy ? 'Starter…' : 'Start'}
        </button>
        <button
          type="button"
          className="btn-ghost text-xs"
          disabled={busy}
          onClick={() => setOpen(false)}
        >
          Avbryt
        </button>
      </div>
    </div>
  );
}

export function OrganizationsSection({
  canWrite,
  canGrant = canWrite,
  canImpersonate = canWrite,
  loadList = fetchOrganizations,
  loadDetail = fetchOrganization,
  loadUsers = fetchPlatformUsers,
  create = createOrganization,
  grant = grantOrganizationRole,
  invite = inviteToOrganization,
  revoke = revokeOrganizationRole,
  upsertLink = upsertOrganizationLink,
  removeLink = deleteOrganizationLink,
}: OrganizationsSectionProps) {
  const [list, setList] = useState<OrganizationsResult | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<OrganizationDetailResult | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState('');

  // The list, filtered by the search box and grouped by kind: centres first,
  // because they own the courts the clubs play on. Derived, so a reload or a
  // new organization lands in the right group by itself.
  const groups = useMemo(() => {
    if (list?.state !== 'ok') return [];
    const q = query.trim().toLowerCase();
    const matching = list.organizations.filter(
      (o) => q === '' || o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q),
    );
    return (['CENTRE', 'CLUB'] as const)
      .map((kind) => ({
        kind,
        label: kind === 'CENTRE' ? 'Sentre' : 'Klubber',
        items: matching.filter((o) => o.kind === kind),
      }))
      .concat([
        {
          kind: 'OTHER' as never,
          label: 'Andre',
          items: matching.filter((o) => o.kind !== 'CENTRE' && o.kind !== 'CLUB'),
        },
      ])
      .filter((g) => g.items.length > 0);
  }, [list, query]);

  const reloadList = useCallback(async () => setList(await loadList()), [loadList]);
  const reloadDetail = useCallback(
    async (id: string) => setDetail(await loadDetail(id)),
    [loadDetail],
  );

  useEffect(() => {
    void reloadList();
  }, [reloadList]);
  useEffect(() => {
    if (selected) void reloadDetail(selected);
    else setDetail(null);
  }, [selected, reloadDetail]);

  const act = async (op: () => Promise<WriteResult>) => {
    setNotice(null);
    const res = await op();
    const err = describe(res);
    if (err) setNotice(err);
    else {
      await reloadList();
      if (selected) await reloadDetail(selected);
    }
    return err === null;
  };

  return (
    <section className="card p-6" data-testid="sa-orgs">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-lg font-semibold text-ink">Organisasjoner</h2>
          <p className="text-xs text-ink-3 mt-1 max-w-2xl">
            Sentre eier baner og booking. Klubber lager aktiviteter på sentrenes baner. Her ser du
            hvem som har roller hvor, og hvilke klubber et senter har gitt tilgang.
          </p>
        </div>
        {canWrite && list?.state === 'ok' && (
          <button
            type="button"
            className="btn-outline text-xs shrink-0"
            onClick={() => setCreating((v) => !v)}
            data-testid="sa-orgs-new"
          >
            {creating ? 'Avbryt' : '+ Ny organisasjon'}
          </button>
        )}
      </div>

      {notice && (
        <p
          className="mt-3 text-[11px] text-rose px-3 py-2 rounded bg-rose/10 border border-rose/20"
          data-testid="sa-orgs-notice"
        >
          {notice}
        </p>
      )}

      {creating && (
        <NewOrganizationForm
          onCreate={async (input) => {
            const ok = await act(() => create(input));
            if (ok) setCreating(false);
          }}
        />
      )}

      {list?.state === 'no-api' && (
        <p className="mt-3 text-[11px] text-ink-3" data-testid="sa-orgs-state">
          API-et er ikke koblet til i denne versjonen ennå.
        </p>
      )}
      {list?.state === 'forbidden' && (
        <p className="mt-3 text-[11px] text-rose" data-testid="sa-orgs-state">
          Du har ikke tilgang til organisasjonene.
        </p>
      )}
      {list?.state === 'error' && (
        <p className="mt-3 text-[11px] text-rose" data-testid="sa-orgs-state">
          Kunne ikke hente organisasjoner{list.status ? ` (HTTP ${list.status})` : ''}.
        </p>
      )}

      {list?.state === 'ok' && (
        <div className="mt-5 grid gap-5 md:grid-cols-[17rem_1fr] items-start">
          {/* THE LIST: searchable, grouped by kind, the selected one marked
              with a lime edge. It is a directory, so it looks like one. */}
          <div className="rounded-xl border border-line bg-paper overflow-hidden">
            <div className="p-2 border-b border-line">
              <input
                type="search"
                className="input w-full text-xs"
                placeholder="Søk senter eller klubb"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Søk organisasjon"
                data-testid="sa-orgs-search"
              />
            </div>
            <ul className="max-h-[70vh] overflow-auto" data-testid="sa-orgs-list">
              {groups.map((g) => (
                <li key={g.kind}>
                  <div className="px-3 pt-3 pb-1 font-mono text-[10px] uppercase tracking-wider text-ink-3">
                    {g.label} <span className="text-ink-4">{g.items.length}</span>
                  </div>
                  <ul>
                    {g.items.map((o) => (
                      <li key={o.id}>
                        <button
                          type="button"
                          onClick={() => setSelected(o.id)}
                          aria-current={selected === o.id ? 'true' : undefined}
                          className={`w-full text-left px-3 py-2 border-l-2 transition-colors ${
                            selected === o.id
                              ? 'border-lime-deep bg-lime-bg/60'
                              : 'border-transparent hover:bg-bg-soft'
                          }`}
                          data-testid={`sa-org-${o.slug}`}
                        >
                          <div className="text-xs font-semibold text-ink truncate">{o.name}</div>
                          <div className="text-[10px] text-ink-3 font-mono">
                            {KIND_LABEL[o.kind] ?? o.kind} · {o.memberCount} med rolle ·{' '}
                            {o.linkCount} {o.kind === 'CENTRE' ? 'klubber' : 'sentre'}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
              {list.organizations.length === 0 && (
                <li className="px-3 py-3 text-[11px] text-ink-3">Ingen organisasjoner ennå.</li>
              )}
              {list.organizations.length > 0 && groups.length === 0 && (
                <li className="px-3 py-3 text-[11px] text-ink-3">Ingen treff på «{query}».</li>
              )}
            </ul>
          </div>

          <div className="min-w-0">
            {!selected && (
              <div className="rounded-xl border border-dashed border-line px-5 py-10 text-center text-xs text-ink-3">
                Velg en organisasjon i listen for å se hvem som har roller der, hvilke samarbeid den
                har, og betalingskontoen.
              </div>
            )}
            {detail?.state === 'ok' && (
              <OrganizationDetailView
                detail={detail.detail}
                allOrganizations={list.organizations}
                canWrite={canWrite}
                canGrant={canGrant}
                canImpersonate={canImpersonate}
                loadUsers={loadUsers}
                onReload={() => reloadDetail(detail.detail.organization.id)}
                onInvite={async (input) => {
                  const res = await invite(detail.detail.organization.id, input);
                  // Reloaded on success so the member list below is what the
                  // SERVER has, not an optimistic guess about it.
                  if (res.state === 'ok') await reloadDetail(detail.detail.organization.id);
                  return res;
                }}
                onGrant={(userId, roleId) =>
                  act(() => grant(detail.detail.organization.id, userId, roleId))
                }
                onRevoke={(userId, roleId) =>
                  act(() => revoke(detail.detail.organization.id, userId, roleId))
                }
                onUpsertLink={(clubId, terms) =>
                  act(() => upsertLink(detail.detail.organization.id, clubId, terms))
                }
                onRemoveLink={(clubId) =>
                  act(() => removeLink(detail.detail.organization.id, clubId))
                }
              />
            )}
            {detail && detail.state !== 'ok' && (
              <p className="text-[11px] text-rose">Kunne ikke hente organisasjonen.</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function OrganizationDetailView({
  detail,
  allOrganizations,
  canWrite,
  canGrant,
  canImpersonate,
  loadUsers,
  onGrant,
  onInvite,
  onRevoke,
  onUpsertLink,
  onRemoveLink,
  onReload,
}: {
  detail: OrganizationDetail;
  allOrganizations: { id: string; slug: string; name: string; kind: string }[];
  canWrite: boolean;
  canGrant: boolean;
  canImpersonate: boolean;
  loadUsers: typeof fetchPlatformUsers;
  onGrant: (userId: string, roleId: string) => Promise<boolean>;
  onInvite: (input: {
    email: string;
    displayName?: string;
    roleId: string;
  }) => Promise<InviteResult>;
  onRevoke: (userId: string, roleId: string) => Promise<boolean>;
  onUpsertLink: (
    clubId: string,
    terms: Parameters<typeof upsertOrganizationLink>[2],
  ) => Promise<boolean>;
  onRemoveLink: (clubId: string) => Promise<boolean>;
  /** Re-read the detail from the server after a write that changed it. */
  onReload: () => Promise<void>;
}) {
  const org = detail.organization;
  const isCentre = org.kind === 'CENTRE';
  const rate = (ore: number | null) => (ore != null ? `${(ore / 100).toFixed(0)} kr/time` : '—');

  return (
    <div data-testid="sa-org-detail" className="rounded-xl border border-line bg-paper">
      {/* THE HEADER: what this is, and the one action that acts on the whole
          organization. The action sits beside the name, not above it (#428
          review) — above, a borderless button read as part of the heading. */}
      <header className="flex items-start justify-between gap-3 flex-wrap px-5 py-4 border-b border-line bg-bg-soft/60">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <KindPill kind={org.kind} />
            <span className="font-mono text-[10px] text-ink-3">{org.slug}</span>
          </div>
          <h3 className="font-display text-xl font-semibold text-ink mt-1 truncate">{org.name}</h3>
        </div>
        <WorkAsOrganization organization={org} canWrite={canImpersonate} />
      </header>

      <div className="px-5 py-5 space-y-6">
        <DetailBlock
          title="Personer med rolle"
          lead="Ansatte og administratorer som har en rolle i organisasjonen. Spillere og medlemmer vises ikke her."
        >
          <PanelTable columns={['Person', 'E-post', 'Roller']} testId="sa-org-members">
            {detail.members.map((m) => (
              <tr key={m.userId} className="border-t border-line-soft align-top">
                <td className="px-3 py-2 font-medium text-ink whitespace-nowrap">
                  {m.displayName ?? '(uten navn)'}
                </td>
                <td className="px-3 py-2 text-ink-3 break-all">{m.email ?? '—'}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {m.roles.map((r) => (
                      <span
                        key={r.id}
                        className="inline-flex items-center gap-1 rounded-full border border-line bg-paper px-2 py-0.5 text-[11px]"
                      >
                        {r.name}
                        {canGrant && (
                          <button
                            type="button"
                            className="text-ink-3 hover:text-rose leading-none"
                            aria-label={`Fjern ${r.name} fra ${m.displayName ?? m.email ?? m.userId}`}
                            title={`Fjern ${r.name}`}
                            onClick={() => void onRevoke(m.userId, r.id)}
                            data-testid={`sa-org-revoke-${m.userId}-${r.key}`}
                          >
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {detail.members.length === 0 && (
              <EmptyRow colSpan={3}>Ingen har roller her ennå.</EmptyRow>
            )}
          </PanelTable>

          {canGrant && (
            <AddPanel
              title="Legg til person"
              lead="Finn en som allerede har konto, eller inviter en ny. Rollen kan alltid tas bort igjen over."
            >
              <div className="grid gap-5 md:grid-cols-2">
                <GrantForm roles={detail.availableRoles} loadUsers={loadUsers} onGrant={onGrant} />
                {/* BESIDE THE SEARCH: finding somebody who already exists is
                    the commoner act and inviting is the fallback when they do
                    not, so the search comes first in reading order. */}
                <InviteForm roles={detail.availableRoles} onInvite={onInvite} />
              </div>
            </AddPanel>
          )}
        </DetailBlock>

        {isCentre && (
          <DetailBlock
            title="Klubber senteret samarbeider med"
            lead="Hva hver side gir den andre, og på hvilke vilkår. Senteret og klubben gjør dette selv under «Samarbeid» i innstillingene; her kan Starpoint gjøre det som support."
          >
            <PanelTable
              columns={['Klubb', 'Senteret gir', 'Klubben gir', 'Oppgjør', 'Pris', 'Status', '']}
              testId="sa-org-links"
            >
              {detail.links.asCentre.map((l) => (
                <tr key={l.id} className="border-t border-line-soft">
                  <td className="px-3 py-2 font-medium text-ink whitespace-nowrap">
                    {l.club?.name}
                  </td>
                  <td className="px-3 py-2">
                    {l.centreGrantsRole ? (
                      l.centreGrantsRole.name
                    ) : (
                      <span className="text-ink-3 italic">ingenting</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {l.clubGrantsRole ? (
                      l.clubGrantsRole.name
                    ) : (
                      <span className="text-ink-3 italic">ingenting</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-ink-2">
                    {SETTLEMENT_LABEL[l.settlementModel] ?? l.settlementModel}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{rate(l.agreedCourtRateOre)}</td>
                  <td className="px-3 py-2">
                    {l.status === 'suspended' ? (
                      <span className="rounded-full bg-amber-50 border border-amber-200 px-2 py-px text-[10px] text-amber-900">
                        Suspendert
                      </span>
                    ) : l.status === 'proposed' ? (
                      <span className="rounded-full bg-bg-soft border border-line px-2 py-px text-[10px] text-ink-2">
                        Venter på klubben
                      </span>
                    ) : (
                      <span className="rounded-full bg-lime-bg border border-lime-deep/40 px-2 py-px text-[10px] text-ink">
                        Aktiv
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {canWrite && l.club && (
                      <button
                        type="button"
                        className="text-[11px] text-ink-3 hover:text-rose underline-offset-2 hover:underline"
                        onClick={() => void onRemoveLink(l.club!.id)}
                      >
                        Fjern
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {detail.links.asCentre.length === 0 && (
                <EmptyRow colSpan={7}>Ingen samarbeid ennå.</EmptyRow>
              )}
            </PanelTable>
            {canWrite && (
              <LinkForm
                clubs={allOrganizations.filter((o) => o.kind === 'CLUB')}
                linkRoles={detail.linkRoles}
                onSubmit={onUpsertLink}
              />
            )}
          </DetailBlock>
        )}

        {!isCentre && (
          <DetailBlock
            title="Sentre klubben samarbeider med"
            lead="Senteret foreslår og setter vilkårene; klubben sier ja og velger hva senteret får hos den."
          >
            <PanelTable
              columns={['Senter', 'Senteret gir', 'Klubben gir', 'Oppgjør', 'Pris']}
              testId="sa-org-links"
            >
              {detail.links.asClub.map((l) => (
                <tr key={l.id} className="border-t border-line-soft">
                  <td className="px-3 py-2 font-medium text-ink whitespace-nowrap">
                    {l.centre?.name}
                  </td>
                  <td className="px-3 py-2">
                    {l.centreGrantsRole ? (
                      l.centreGrantsRole.name
                    ) : (
                      <span className="text-ink-3 italic">ingenting</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {l.clubGrantsRole ? (
                      l.clubGrantsRole.name
                    ) : (
                      <span className="text-ink-3 italic">ingenting</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-ink-2">
                    {SETTLEMENT_LABEL[l.settlementModel] ?? l.settlementModel}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{rate(l.agreedCourtRateOre)}</td>
                </tr>
              ))}
              {detail.links.asClub.length === 0 && (
                <EmptyRow colSpan={5}>
                  Ingen sentre ennå. Et samarbeid foreslås fra senterets side.
                </EmptyRow>
              )}
            </PanelTable>
          </DetailBlock>
        )}

        <DetailBlock
          title="Betalingskonto"
          lead="Kontoen hos betalingsleverandøren som organisasjonens betalinger går til."
        >
          <StripeAccountField
            organizationId={org.id}
            current={org.stripeAccountId}
            canWrite={canWrite}
            onSaved={onReload}
          />
        </DetailBlock>
      </div>
    </div>
  );
}

function GrantForm({
  roles,
  loadUsers,
  onGrant,
}: {
  roles: { id: string; key: string; name: string }[];
  loadUsers: typeof fetchPlatformUsers;
  onGrant: (userId: string, roleId: string) => Promise<boolean>;
}) {
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [userId, setUserId] = useState('');
  const [roleId, setRoleId] = useState(roles[0]?.id ?? '');
  const [busy, setBusy] = useState(false);

  // A NAME OR AN E-MAIL IS REQUIRED (#428 review). The directory's default
  // listing is StarPoint's own people, so an empty search here would offer a
  // list of colleagues for a control whose whole purpose is granting a role to
  // a CUSTOMER. Searching for the person is the correct gesture anyway; this
  // just stops the empty one from answering something misleading.
  const canSearch = query.trim() !== '';
  const search = async () => {
    if (!canSearch) return;
    const res = await loadUsers(query.trim());
    setUsers(res.state === 'ok' ? res.users : []);
  };

  return (
    <div className="min-w-0" data-testid="sa-org-grant">
      <div className="text-xs font-semibold text-ink">Finn eksisterende konto</div>
      <form
        className="mt-2 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void search();
        }}
      >
        <input
          type="search"
          className="input flex-1 min-w-0 text-xs"
          placeholder="Navn eller e-post"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="sa-org-grant-search"
        />
        <button type="submit" className="btn-outline text-xs" disabled={!canSearch}>
          Søk
        </button>
      </form>
      {users.length > 0 && (
        <div className="mt-3 grid gap-2">
          <Field label="Konto">
            <select
              className="input w-full text-xs"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              data-testid="sa-org-grant-user"
            >
              <option value="">Velg konto…</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName ?? u.email ?? u.id}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Rolle">
            <select
              className="input w-full text-xs"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              data-testid="sa-org-grant-role"
            >
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>
          <div>
            <button
              type="button"
              className="btn-lime text-xs"
              disabled={!userId || !roleId || busy}
              onClick={async () => {
                setBusy(true);
                try {
                  if (await onGrant(userId, roleId)) setUserId('');
                } finally {
                  setBusy(false);
                }
              }}
              data-testid="sa-org-grant-submit"
            >
              Gi rolle
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * INVITE SOMEBODY WHO IS NOT IN THE DIRECTORY YET.
 *
 * `GrantForm` above finds an account that already exists. This is the other
 * half, and it is the one that removes a developer from the loop: creating a
 * club administrator used to need a checkout, an Infisical login and a
 * password piped through stdin.
 *
 * THE OPERATOR NEVER TYPES A CREDENTIAL, and there is none to type: passwords
 * were retired in #754. The server creates the account with no credential and
 * mails an invitation that says to sign in with the address and a one-time
 * code — the address IS the way in (#1009 corrected the copy here, which
 * still promised a password link).
 *
 * IT IS SAFE TO USE ON SOMEBODY WHO ALREADY HAS AN ACCOUNT. The
 * server recognises a LOCAL identity with that address, skips creation,
 * grants the membership and sends a different mail — so an operator who
 * cannot remember whether a club person already has an account can just
 * invite them. That is the common case when a club's second centre is added.
 *
 * AN ADDRESS THAT BELONGS TO AN ENTRA ACCOUNT IS REFUSED, 409
 * `identity_ambiguous` (#545). An ENTRA-only user (staff, signed in through
 * Microsoft) has no LOCAL identity, and this used to make them a second,
 * separate account. The server now refuses instead, and the form says so in a
 * sentence — from the catalog, nb and en — rather than as a code. Linking the
 * two sign-ins is a later decision (#437), so the text offers no remedy the
 * system does not have.
 *
 * Since #1047 the same code answers an address another account already
 * carries without an e-mail sign-in for it, and `email_in_use` (409) answers
 * the race in which another account took the address mid-request. Both are
 * sentences from the catalog, never the code.
 */
function InviteForm({
  roles,
  onInvite,
}: {
  roles: { id: string; key: string; name: string }[];
  onInvite: (input: {
    email: string;
    displayName?: string;
    roleId: string;
  }) => Promise<InviteResult>;
}) {
  const t = useT();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roleId, setRoleId] = useState(roles[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<InviteResult | null>(null);

  const ready = email.trim() !== '' && roleId !== '' && !busy;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setDone(null);
    const res = await onInvite({
      email: email.trim(),
      displayName: displayName.trim() || undefined,
      roleId,
    });
    setBusy(false);
    setDone(res);
    if (res.state === 'ok') {
      setEmail('');
      setDisplayName('');
    }
  };

  return (
    <form className="min-w-0" data-testid="sa-org-invite" onSubmit={(e) => void submit(e)}>
      <div className="text-xs font-semibold text-ink">Inviter ny bruker</div>
      <p className="mt-0.5 text-[11px] text-ink-3" data-testid="sa-org-invite-lead">
        {t('saOrgInvite.lead')}
      </p>
      <div className="mt-2 grid gap-2">
        <Field label="E-post">
          <input
            type="email"
            className="input w-full text-xs"
            placeholder="navn@klubb.no"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="sa-org-invite-email"
          />
        </Field>
        <Field label="Navn (valgfritt)">
          <input
            className="input w-full text-xs"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            data-testid="sa-org-invite-name"
          />
        </Field>
        <Field label="Rolle">
          <select
            className="input w-full text-xs"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            data-testid="sa-org-invite-role"
          >
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
        <div>
          <button type="submit" className="btn-lime text-xs" disabled={!ready}>
            {busy ? 'Sender…' : 'Inviter'}
          </button>
        </div>
      </div>

      {/* WHAT ACTUALLY HAPPENED, in the operator's words. «Invited» and
          «already had an account» are different outcomes, and telling somebody
          to expect a mail that was never sent is how a person waits a day for
          nothing. `mailed` is false on a deployment with no mail configured,
          and saying so is better than implying one is on its way. */}
      {done && (
        <p className="mt-2 text-[11px] text-ink-3" data-testid="sa-org-invite-result">
          {done.state === 'ok'
            ? done.created
              ? done.mailed
                ? t('saOrgInvite.createdMailed')
                : t('saOrgInvite.createdNotMailed')
              : done.mailed
                ? 'Kontoen fantes fra før. Tilgang er gitt, og personen har fått beskjed.'
                : 'Kontoen fantes fra før. Tilgang er gitt.'
            : done.state === 'refused'
              ? done.reason === 'identity_ambiguous'
                ? t('platform.organizations.invite.identityAmbiguous')
                : done.reason === 'email_in_use'
                  ? t('platform.organizations.invite.emailInUse')
                  : `Serveren avslo invitasjonen (${done.reason}).`
              : done.state === 'forbidden'
                ? 'Du har ikke tilgang til å invitere her.'
                : 'Kunne ikke sende invitasjonen.'}
        </p>
      )}
    </form>
  );
}

function LinkForm({
  clubs,
  linkRoles,
  onSubmit,
}: {
  clubs: { id: string; name: string }[];
  linkRoles: { id: string; name: string }[];
  onSubmit: (
    clubId: string,
    terms: Parameters<typeof upsertOrganizationLink>[2],
  ) => Promise<boolean>;
}) {
  const [clubId, setClubId] = useState('');
  const [centreGives, setCentreGives] = useState('');
  const [clubGives, setClubGives] = useState('');
  const [settlementModel, setSettlementModel] = useState<'centre_invoice' | 'direct'>(
    'centre_invoice',
  );
  const [rate, setRate] = useState('');
  const [busy, setBusy] = useState(false);
  if (clubs.length === 0) {
    return (
      <p className="mt-3 text-[11px] text-ink-3">Opprett en klubb først for å lage et samarbeid.</p>
    );
  }
  return (
    <AddPanel
      title="Lag eller endre et samarbeid, som support"
      lead="Hver side gir en samarbeidsrolle fra Rettigheter. Et samarbeid laget her er aktivt med en gang. Vilkårene er data — ingen penger flyttes."
      testId="sa-org-link-form"
    >
      {/* TWO COLUMNS, NOT FIVE. The panel is about 700px wide beside the
          list; five labelled fields on one row shrank the selects to two
          letters and ran the captions into each other. */}
      <div className="grid gap-3 sm:grid-cols-2 items-end">
        <Field label="Klubb">
          <select
            className="input w-full text-xs"
            value={clubId}
            onChange={(e) => setClubId(e.target.value)}
          >
            <option value="">Velg klubb…</option>
            {clubs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Senteret gir klubben">
          <select
            className="input w-full text-xs"
            value={centreGives}
            onChange={(e) => setCentreGives(e.target.value)}
            data-testid="sa-org-link-centre-gives"
          >
            <option value="">Ingenting</option>
            {linkRoles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Klubben gir senteret">
          <select
            className="input w-full text-xs"
            value={clubGives}
            onChange={(e) => setClubGives(e.target.value)}
            data-testid="sa-org-link-club-gives"
          >
            <option value="">Ingenting</option>
            {linkRoles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Oppgjør">
          <select
            className="input w-full text-xs"
            value={settlementModel}
            onChange={(e) => setSettlementModel(e.target.value as typeof settlementModel)}
          >
            <option value="centre_invoice">På senterets faktura</option>
            <option value="direct">Direkte avtale med Starpoint</option>
          </select>
        </Field>
        <Field label="Pris kr/time">
          <input
            className="input w-full text-xs"
            type="number"
            min={0}
            placeholder="450"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />
        </Field>
        <div className="sm:col-span-2">
          <button
            type="button"
            className="btn-lime text-xs"
            disabled={!clubId || busy}
            onClick={async () => {
              setBusy(true);
              try {
                const ore = rate.trim() ? Math.round(Number(rate) * 100) : null;
                await onSubmit(clubId, {
                  centreGrantsRoleId: centreGives || null,
                  clubGrantsRoleId: clubGives || null,
                  settlementModel,
                  agreedCourtRateOre: Number.isFinite(ore) ? ore : null,
                  status: 'active',
                });
              } finally {
                setBusy(false);
              }
            }}
          >
            Lagre samarbeidet
          </button>
        </div>
      </div>
    </AddPanel>
  );
}

function NewOrganizationForm({
  onCreate,
}: {
  onCreate: (input: { slug: string; name: string; kind: 'CENTRE' | 'CLUB' }) => Promise<void>;
}) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'CENTRE' | 'CLUB'>('CLUB');
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="mt-4 rounded-lg border border-line bg-bg-soft/50 p-4"
      data-testid="sa-orgs-new-form"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          await onCreate({ slug: slug.trim().toLowerCase(), name: name.trim(), kind });
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-2">
        Ny organisasjon
      </div>
      <p className="mt-0.5 text-[11px] text-ink-3">
        Et senter eier baner og booking. En klubb lager aktiviteter. Typen kan ikke endres etterpå.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_9rem_auto] items-end">
        <Field label="Navn">
          <input
            className="input w-full text-xs"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Oslo Padelsenter"
            data-testid="sa-orgs-new-name"
          />
        </Field>
        <Field label="Kortnavn (URL)">
          <input
            className="input w-full text-xs font-mono"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="oslo-padel"
            data-testid="sa-orgs-new-slug"
          />
        </Field>
        <Field label="Type">
          <select
            className="input w-full text-xs"
            value={kind}
            onChange={(e) => setKind(e.target.value as typeof kind)}
          >
            <option value="CLUB">Klubb</option>
            <option value="CENTRE">Senter</option>
          </select>
        </Field>
        <button type="submit" className="btn-lime text-xs" disabled={busy || !slug || !name}>
          {busy ? 'Oppretter…' : 'Opprett'}
        </button>
      </div>
    </form>
  );
}

/**
 * Which PSP account this organization's payments land in (#284).
 *
 * WHY IT IS HERE AND NOT IN A CLUB'S OWN SETTINGS: it decides whose bank account
 * receives a member's money, so it is platform administration. A club
 * administrator changing it would be choosing where their own revenue goes,
 * which is a different conversation from configuring an app.
 *
 * EITHER KIND MAY HAVE ONE. A centre is Merchant of Record for court time, a club
 * for what a club sells — so there is no check on `kind` here, and none on the
 * server.
 *
 * NOT A SECRET, so it is shown in full rather than masked: an `acct_…` identifies
 * an account, it does not authorise anything. Masking it would only make the one
 * check anybody needs — «is this the right account?» — impossible to do by eye.
 */
function StripeAccountField({
  organizationId,
  current,
  canWrite,
  onSaved,
}: {
  organizationId: string;
  current: string | null;
  canWrite: boolean;
  onSaved: () => Promise<void>;
}) {
  const t = useT();
  const [value, setValue] = useState(current ?? '');
  const [editing, setEditing] = useState(false);
  // FOCUS MOVES TO THE FIELD WHEN «ENDRE» OPENS IT — what `autoFocus` did, as
  // an effect on the user's own action rather than an attribute (#900): the
  // same keyboard result, and the field is never focused by a mount nobody
  // asked for.
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);
  const [refusal, setRefusal] = useState<string | null>(null);
  /** The repoint awaiting confirmation — see `submit` below. */
  const [pendingReplacement, setPendingReplacement] = useState<string | null>(null);
  // The argument tuple is explicit because `useAsyncAction` infers `unknown[]`,
  // which would make `replace` untyped exactly where it decides whether a live
  // payment account may move.
  const save = useAsyncAction<[string | null, boolean?]>(async (next, replace = false) => {
    setRefusal(null);
    const res = await setOrganizationStripeAccount(organizationId, next, { replace });
    if (res.state === 'refused') {
      setRefusal(REFUSAL_TEXT[res.reason] ?? res.reason);
      return;
    }
    if (res.state !== 'ok') {
      setRefusal('Kunne ikke lagre. Prøv igjen.');
      return;
    }
    setPendingReplacement(null);
    setEditing(false);
    await onSaved();
  });

  /**
   * MOVING A LIVE ACCOUNT ASKS FIRST, and names both accounts when it asks.
   *
   * Connecting an organization that has none, and disconnecting one on purpose,
   * are ordinary saves. Replacing a live account is the one change whose damage is
   * invisible: the next payment succeeds and arrives in a different bank account.
   * The CLI spells that intent `--replace`; the same move must not be cheaper here
   * just because it is a page.
   *
   * The server refuses an unconfirmed repoint regardless of what this does — this
   * is how the operator is asked, not how the rule is enforced. No
   * `window.confirm()`: it is banned repo-wide, and a browser dialog could not show
   * the two account ids that make the question answerable anyway.
   */
  const submit = (raw: string) => {
    const next = raw.trim() === '' ? null : raw.trim();
    if (current !== null && next !== null && next !== current) {
      setRefusal(null);
      setPendingReplacement(next);
      return;
    }
    void save.run(next);
  };

  return (
    <div>
      {!editing ? (
        <div className="flex items-center gap-3 flex-wrap">
          <span
            className={`font-mono text-xs rounded-md border px-2 py-1 ${
              current
                ? 'border-line bg-bg-soft/60 text-ink'
                : 'border-dashed border-line text-ink-3'
            }`}
            data-testid="sa-org-stripe-account"
          >
            {current ?? 'Ikke koblet'}
          </span>
          {canWrite && (
            <button
              type="button"
              onClick={() => {
                setValue(current ?? '');
                setRefusal(null);
                setPendingReplacement(null);
                setEditing(true);
              }}
              className="text-[11px] font-mono uppercase tracking-wider text-ink-3 hover:text-ink"
              data-testid="sa-org-stripe-edit"
            >
              {current ? 'Endre' : 'Koble til'}
            </button>
          )}
        </div>
      ) : (
        <div className="mt-1 flex items-start gap-2 flex-wrap">
          <input
            ref={inputRef}
            // A placeholder is not a name.
            aria-label={t('platform.organizations.stripeAccountLabel')}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="acct_…"
            className="input font-mono text-xs"
            data-testid="sa-org-stripe-input"
          />
          <button
            type="button"
            disabled={save.pending}
            onClick={() => submit(value)}
            className="btn-lime text-xs disabled:opacity-40"
            data-testid="sa-org-stripe-save"
          >
            {save.pending ? 'Lagrer…' : 'Lagre'}
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              setRefusal(null);
              setPendingReplacement(null);
            }}
            className="text-[11px] font-mono uppercase tracking-wider text-ink-3 hover:text-ink px-2 py-1"
          >
            Avbryt
          </button>
          {/* AN EMPTY FIELD DISCONNECTS, said out loud — «save nothing» reading as
              «cancel» is how somebody removes a payment account by accident. */}
          <p className="basis-full text-[11px] text-ink-3">
            Tomt felt kobler fra. Organisasjonen kan da ikke ta imot betaling.
          </p>
          {/* THE CONFIRMATION NAMES BOTH ACCOUNTS. «Er du sikker?» is unanswerable
              on a money-routing change; the two ids are the whole question, so they
              are shown in full and the consequence is stated as what happens next
              rather than as a warning. */}
          {pendingReplacement !== null && (
            <div
              className="basis-full mt-1 border border-rose/40 bg-rose/5 p-2"
              data-testid="sa-org-stripe-confirm"
            >
              <p className="text-[11px] text-ink">
                Bytter betalingskonto fra <span className="font-mono">{current}</span> til{' '}
                <span className="font-mono">{pendingReplacement}</span>. Fra neste betaling går
                pengene til den nye kontoen.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  disabled={save.pending}
                  onClick={() => void save.run(pendingReplacement, true)}
                  className="btn-lime text-xs disabled:opacity-40"
                  data-testid="sa-org-stripe-confirm-save"
                >
                  {save.pending ? 'Bytter…' : 'Bekreft bytte'}
                </button>
                <button
                  type="button"
                  onClick={() => setPendingReplacement(null)}
                  className="text-[11px] font-mono uppercase tracking-wider text-ink-3 hover:text-ink px-2 py-1"
                  data-testid="sa-org-stripe-confirm-cancel"
                >
                  Behold {current}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Beside the control, not in a banner (#369). */}
      {refusal && (
        <p role="alert" className="mt-1 text-[11px] font-medium text-rose">
          {refusal}
        </p>
      )}
    </div>
  );
}
